/**
 * RecurrenceRuleBuilder
 *
 * Converts between our RecurrenceRule type and RFC 5545 RRULE format.
 * Also provides human-readable descriptions of recurrence rules.
 *
 * RFC 5545 Reference: https://tools.ietf.org/html/rfc5545#section-3.3.10
 *
 * Follows single responsibility principle: only handles recurrence rule
 * conversion and formatting.
 */

import type {
  RecurrenceRule,
  RecurrenceFrequency,
  DayOfWeek,
  RecurrenceEndCondition,
} from '@/types/events';

/**
 * RecurrenceRuleBuilder
 *
 * Handles conversion between RecurrenceRule and RRULE string format,
 * plus human-readable descriptions.
 */
export class RecurrenceRuleBuilder {
  /**
   * Convert RecurrenceRule to RFC 5545 RRULE string
   *
   * Example outputs:
   * - "FREQ=DAILY;INTERVAL=1"
   * - "FREQ=WEEKLY;INTERVAL=2;BYDAY=MO,WE,FR"
   * - "FREQ=MONTHLY;INTERVAL=1;BYMONTHDAY=15;COUNT=10"
   * - "FREQ=YEARLY;INTERVAL=1;BYMONTH=12;BYMONTHDAY=25"
   */
  static buildRRule(rule: RecurrenceRule): string {
    const parts: string[] = [];

    // Frequency (required)
    parts.push(`FREQ=${rule.frequency}`);

    // Interval (only if not 1)
    if (rule.interval > 1) {
      parts.push(`INTERVAL=${rule.interval}`);
    }

    // By day (for weekly)
    if (rule.byDay && rule.byDay.length > 0) {
      parts.push(`BYDAY=${rule.byDay.join(',')}`);
    }

    // By month day (for monthly)
    if (rule.byMonthDay !== undefined && rule.byMonthDay !== null) {
      parts.push(`BYMONTHDAY=${rule.byMonthDay}`);
    }

    // By set position (for ordinal weekday: e.g., "2nd Tuesday")
    if (rule.bySetPos !== undefined && rule.bySetPos !== null) {
      parts.push(`BYSETPOS=${rule.bySetPos}`);
    }

    // By month (for yearly)
    if (rule.byMonth !== undefined && rule.byMonth !== null) {
      parts.push(`BYMONTH=${rule.byMonth}`);
    }

    // End condition
    if (rule.endCondition.type === 'count') {
      parts.push(`COUNT=${rule.endCondition.count}`);
    } else if (rule.endCondition.type === 'until') {
      // Convert yyyy-mm-dd to YYYYMMDD format
      const dateStr = rule.endCondition.until.replace(/-/g, '');
      parts.push(`UNTIL=${dateStr}`);
    }

    return parts.join(';');
  }

  /**
   * Parse RFC 5545 RRULE string to RecurrenceRule
   *
   * Returns null if parsing fails.
   */
  static parseRRule(rrule: string): RecurrenceRule | null {
    try {
      // Remove "RRULE:" prefix if present
      const ruleStr = rrule.replace(/^RRULE:/i, '').trim();

      const parts = ruleStr.split(';');
      const ruleParts: Record<string, string> = {};

      parts.forEach((part) => {
        const [key, value] = part.split('=');
        if (key && value) {
          ruleParts[key.toUpperCase()] = value;
        }
      });

      // Frequency is required
      const freq = ruleParts.FREQ as RecurrenceFrequency | undefined;
      if (!freq || !this.isValidFrequency(freq)) {
        return null;
      }

      // Interval (default 1)
      const interval = ruleParts.INTERVAL
        ? parseInt(ruleParts.INTERVAL, 10)
        : 1;
      if (isNaN(interval) || interval < 1) {
        return null;
      }

      // Optional: By day
      const byDay = ruleParts.BYDAY
        ? (ruleParts.BYDAY.split(',') as DayOfWeek[])
        : undefined;

      // Optional: By month day
      const byMonthDay = ruleParts.BYMONTHDAY
        ? parseInt(ruleParts.BYMONTHDAY, 10)
        : undefined;

      // Optional: By month
      const byMonth = ruleParts.BYMONTH
        ? parseInt(ruleParts.BYMONTH, 10)
        : undefined;

      // Optional: By set position (ordinal weekday)
      const bySetPos = ruleParts.BYSETPOS
        ? parseInt(ruleParts.BYSETPOS, 10)
        : undefined;

      // End condition
      let endCondition: RecurrenceEndCondition;
      if (ruleParts.COUNT) {
        const count = parseInt(ruleParts.COUNT, 10);
        if (isNaN(count) || count < 1) {
          return null;
        }
        endCondition = { type: 'count', count };
      } else if (ruleParts.UNTIL) {
        // Convert YYYYMMDD to yyyy-mm-dd
        const until = ruleParts.UNTIL.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3');
        endCondition = { type: 'until', until };
      } else {
        endCondition = { type: 'never' };
      }

      return {
        frequency: freq,
        interval,
        byDay,
        byMonthDay,
        byMonth,
        bySetPos,
        endCondition,
      };
    } catch (err) {
      console.error('[RecurrenceRuleBuilder] Failed to parse RRULE:', err);
      return null;
    }
  }

  /**
   * Convert RecurrenceRule to human-readable string
   *
   * Examples:
   * - "Daily"
   * - "Every 2 weeks on Monday and Wednesday"
   * - "Monthly on day 15"
   * - "Yearly on December 25"
   * - "Every weekday"
   * - "Daily, 10 times"
   * - "Weekly until Dec 31, 2025"
   */
  static humanizeRRule(rule: RecurrenceRule): string {
    const parts: string[] = [];

    // Frequency and interval
    if (rule.interval === 1) {
      parts.push(this.getFrequencyLabel(rule.frequency));
    } else {
      parts.push(
        `Every ${rule.interval} ${this.getFrequencyUnitLabel(rule.frequency, true)}`,
      );
    }

    // Weekly: Add days
    if (rule.frequency === 'WEEKLY' && rule.byDay && rule.byDay.length > 0) {
      // Special case: Every weekday
      const isWeekdays =
        rule.byDay.length === 5 &&
        ['MO', 'TU', 'WE', 'TH', 'FR'].every((d) =>
          rule.byDay!.includes(d as DayOfWeek),
        );

      if (isWeekdays && rule.interval === 1) {
        return 'Every weekday' + this.getEndConditionSuffix(rule.endCondition);
      }

      const dayLabels = rule.byDay.map(this.getDayLabel);
      parts.push(`on ${this.joinWithAnd(dayLabels)}`);
    }

    // Monthly: Add day or ordinal weekday
    if (rule.frequency === 'MONTHLY' && rule.bySetPos && rule.byDay?.length === 1) {
      const ordinalLabel = this.getOrdinalLabel(rule.bySetPos);
      const dayLabel = this.getDayLabel(rule.byDay[0]);
      parts.push(`on the ${ordinalLabel} ${dayLabel}`);
    } else if (rule.frequency === 'MONTHLY' && rule.byMonthDay) {
      parts.push(`on day ${rule.byMonthDay}`);
    }

    // Yearly: Add month and day
    if (rule.frequency === 'YEARLY') {
      if (rule.byMonth && rule.byMonthDay) {
        const monthLabel = this.getMonthLabel(rule.byMonth);
        parts.push(`on ${monthLabel} ${rule.byMonthDay}`);
      } else if (rule.byMonth) {
        const monthLabel = this.getMonthLabel(rule.byMonth);
        parts.push(`in ${monthLabel}`);
      }
    }

    return parts.join(' ') + this.getEndConditionSuffix(rule.endCondition);
  }

  /**
   * Get end condition suffix for human-readable string
   */
  private static getEndConditionSuffix(
    condition: RecurrenceEndCondition,
  ): string {
    if (condition.type === 'count') {
      return `, ${condition.count} ${condition.count === 1 ? 'time' : 'times'}`;
    } else if (condition.type === 'until') {
      const date = new Date(`${condition.until}T00:00:00`);
      const formatted = date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      return `, until ${formatted}`;
    }
    return '';
  }

  /**
   * Get frequency label (singular form)
   */
  private static getFrequencyLabel(freq: RecurrenceFrequency): string {
    switch (freq) {
      case 'DAILY':
        return 'Daily';
      case 'WEEKLY':
        return 'Weekly';
      case 'MONTHLY':
        return 'Monthly';
      case 'YEARLY':
        return 'Yearly';
    }
  }

  /**
   * Get frequency unit label (plural or singular)
   */
  private static getFrequencyUnitLabel(
    freq: RecurrenceFrequency,
    plural: boolean,
  ): string {
    switch (freq) {
      case 'DAILY':
        return plural ? 'days' : 'day';
      case 'WEEKLY':
        return plural ? 'weeks' : 'week';
      case 'MONTHLY':
        return plural ? 'months' : 'month';
      case 'YEARLY':
        return plural ? 'years' : 'year';
    }
  }

  /**
   * Get day of week label
   */
  private static getDayLabel(day: DayOfWeek): string {
    switch (day) {
      case 'MO':
        return 'Monday';
      case 'TU':
        return 'Tuesday';
      case 'WE':
        return 'Wednesday';
      case 'TH':
        return 'Thursday';
      case 'FR':
        return 'Friday';
      case 'SA':
        return 'Saturday';
      case 'SU':
        return 'Sunday';
    }
  }

  /**
   * Get month label
   */
  private static getMonthLabel(month: number): string {
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    return months[month - 1] ?? 'Unknown';
  }

  /**
   * Join array with "and" for the last item
   */
  private static joinWithAnd(items: string[]): string {
    if (items.length === 0) return '';
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    const last = items[items.length - 1];
    const rest = items.slice(0, -1);
    return `${rest.join(', ')}, and ${last}`;
  }

  /**
   * Get ordinal label for set position (1=first, 2=second, -1=last)
   */
  private static getOrdinalLabel(pos: number): string {
    switch (pos) {
      case 1: return 'first';
      case 2: return 'second';
      case 3: return 'third';
      case 4: return 'fourth';
      case -1: return 'last';
      default: return `${pos}th`;
    }
  }

  /**
   * Check if frequency is valid
   */
  private static isValidFrequency(freq: string): freq is RecurrenceFrequency {
    return ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'].includes(freq);
  }
}
